import { css } from 'lit';
import { Districts } from '@/config/ConfigCARSx';
import { ConfigWZRT } from '@/config/ConfigWZRT';
import { EMAIL_ADDRESS_REGEX } from '@/constants';
import { isLaneImpacted, isValidNumber, isValidString } from '@/utils/utils';
import { DraftEvent, LocationTypes, ProjectContactDto } from '@typings/api';
import { isValidPhoneNumber } from 'libphonenumber-js';

interface Validator {
	label: string;
	validate: (draftEvent: DraftEvent) => boolean;
	shouldIgnore?: (draftEvent: DraftEvent) => boolean;
}

export const projectContactIsEmptyAndOptional = (contact: ProjectContactDto): boolean => {
	const { phoneNumber, email, contactType } = contact;

	if (
		contactType &&
		!ConfigWZRT.form.projectContacts.requiredProjectContactTypes.includes(contactType) &&
		(phoneNumber === '' || phoneNumber == null) &&
		(email === '' || email == null)
	) {
		return true;
	}

	return false;
};

export const projectContactIsValidForWorkZone = (contact: ProjectContactDto): boolean => {
	const { phoneNumber, email } = contact;

	// optional contact types with no phone/email info are valid to submit
	if (projectContactIsEmptyAndOptional(contact)) {
		return true;
	}

	if (!isValidString(phoneNumber) || !isValidPhoneNumber(phoneNumber, 'US')) {
		return false;
	}
	if (email != null && email.trim() !== '' && !EMAIL_ADDRESS_REGEX.test(email)) {
		return false;
	}

	return true;
};

export const workZoneFormValidators: Validator[] = [
	{
		label: 'Event description',
		validate: (draftEvent: DraftEvent) => draftEvent.eventType != null,
	},
	{
		label: 'Route',
		validate: (draftEvent: DraftEvent) => draftEvent.route != null,
	},
	{
		label: 'Start point',
		validate: (draftEvent: DraftEvent) => {
			if (draftEvent.eventLocation === LocationTypes.RAMP) {
				return isValidNumber(draftEvent.rampPk);
			}

			if (draftEvent.eventLocation === LocationTypes.LOCAL_ROAD) {
				return (
					isValidString(draftEvent.route) &&
					isValidString(draftEvent.locationDetails?.crossStreetStart)
				);
			}

			return isValidNumber(draftEvent.startMileMarker);
		},
	},
	{
		label: 'Lane info',
		validate: (draftEvent: DraftEvent) =>
			isLaneImpacted(draftEvent?.positiveLaneBlockage) === true ||
			isLaneImpacted(draftEvent?.negativeLaneBlockage) === true,
		shouldIgnore: (draftEvent: DraftEvent) =>
			draftEvent.eventLocation === LocationTypes.LOCAL_ROAD ||
			draftEvent.eventLocation === LocationTypes.RAMP,
	},
	{
		label: 'Schedule',
		validate: (draftEvent: DraftEvent) =>
			isValidNumber(draftEvent.dateStart) && isValidNumber(draftEvent.dateEnd),
	},
	{
		label: 'Project Contacts',
		validate: (draftEvent: DraftEvent) =>
			(draftEvent.projectContacts?.some(
				(contact) =>
					contact.contactType &&
					ConfigWZRT.form.projectContacts.requiredProjectContactTypes.includes(contact.contactType),
			) &&
				draftEvent.projectContacts?.every(projectContactIsValidForWorkZone)) ??
			false,
	},
	{
		label: 'Restrictions',
		validate: (draftEvent: DraftEvent) =>
			isValidNumber(draftEvent.minRequiredQuantities) &&
			(draftEvent.quantities?.length ?? 0) >= draftEvent.minRequiredQuantities,
	},
	{
		label: 'Lane closure manual',
		validate: (draftEvent: DraftEvent) =>
			!(
				(draftEvent.locationDetails?.district ?? []).some((districtName) =>
					ConfigWZRT.form.affectedLanes.districtsForNonStandardLaneClosures.includes(
						districtName as Districts,
					),
				) && draftEvent.hasNonStandardLaneClosures == null
			),
		shouldIgnore: (draftEvent: DraftEvent) =>
			!(draftEvent.locationDetails?.district ?? []).some((districtName) =>
				ConfigWZRT.form.affectedLanes.districtsForNonStandardLaneClosures.includes(
					districtName as Districts,
				),
			),
	},
];
